import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LeadsTable } from "../components/leads/LeadsTable";
import { BulkAssignModal } from "../components/leads/BulkAssignModal";
import { BulkUnassignModal } from "../components/leads/BulkUnassignModal";
import { MultiSelect } from "../components/ui/multi-select";
import { UserPlus, UserMinus, Search, Filter, ChevronDown, ChevronUp, X } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { fetchAvailableLeads, fetchDistinctValues } from "../services/crmServices/leadService";
import { ComposeEmailDialog } from "../components/mail/ComposeEmailDialog";
import { useAuth } from "../hooks/useAuth";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";

export function LiveLeads() {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [showBulkUnassignModal, setShowBulkUnassignModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState(null);
    const [currentFilters, setCurrentFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [emailLead, setEmailLead] = useState(null);
    
    // Filter options
    const [affiliateOptions, setAffiliateOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    
    // All affiliates with counts (based on current filters, not just current page)
    const [allAffiliatesWithCounts, setAllAffiliatesWithCounts] = useState([]);

    // Add ref to track if component is mounted (for cleanup)
    const isMountedRef = useRef(true);

    // Check if user is admin
    useEffect(() => {
        if (user?.position?.role !== "admin") {
            toast.error("Access denied. Admin privileges required.");
        }
    }, [user]);

    // Load filter options
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const [affiliatesResponse, countriesResponse] = await Promise.all([
                    fetchDistinctValues('affiliate'),
                    fetchDistinctValues('country')
                ]);

                if (affiliatesResponse?.data) {
                    setAffiliateOptions(affiliatesResponse.data.map(aff => ({ label: aff, value: aff })));
                }
                if (countriesResponse?.data) {
                    setCountryOptions(countriesResponse.data.map(country => ({ label: country, value: country })));
                }
                
                // Set status options from Redux
                if (leadStatuses && leadStatuses.length > 0) {
                    setStatusOptions(leadStatuses.map(status => ({ 
                        label: typeof status === 'string' ? status : status.label || status.value, 
                        value: typeof status === 'string' ? status : status.value || status.label 
                    })));
                }
            } catch (error) {
                console.error("Error loading filter options:", error);
            }
        };

        loadFilterOptions();
    }, [leadStatuses]);

    // Fetch affiliate leads (leads with affiliate field) - OPTIMIZED: Server-side filtering
    const fetchLeadsFromServer = useCallback(async () => {
        setLoading(true);
        try {
            // Build filters - add hasAffiliate filter for server-side filtering
            const filters = {
                ...currentFilters,
                hasAffiliate: true, // Server-side filter for leads with affiliate field
                ...(searchTerm.trim() && { searchTerm: searchTerm.trim() })
            };

            // Remove undefined and empty values
            Object.keys(filters).forEach(key => {
                if (filters[key] === undefined || 
                    filters[key] === null ||
                    (Array.isArray(filters[key]) && filters[key].length === 0) ||
                    (typeof filters[key] === 'string' && filters[key].trim() === '')) {
                    delete filters[key];
                }
            });

            // Fetch leads with server-side filtering - use normal pageSize (no 3x multiplier)
            const response = await fetchAvailableLeads(currentPage, pageSize, filters);
            
            if (response?.data) {
                // No client-side filtering needed - backend handles it
                setLeads(response.data);
                setFilteredLeads(response.data);
                
                // Use backend pagination
                if (response.pagination) {
                    setPagination(response.pagination);
                } else {
                    setPagination({
                        page: currentPage,
                        limit: pageSize,
                        totalCount: response.data.length,
                        totalPages: Math.ceil(response.data.length / pageSize)
                    });
                }
            } else {
                setLeads([]);
                setFilteredLeads([]);
                setPagination(null);
            }
        } catch (error) {
            console.error("Error fetching affiliate leads:", error);
            toast.error("Failed to fetch affiliate leads");
            setLeads([]);
            setFilteredLeads([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTerm, currentFilters]);

    useEffect(() => {
        fetchLeadsFromServer();
    }, [fetchLeadsFromServer]);

    // Handle select all
    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            setSelectedLeads([...filteredLeads]);
        } else {
            setSelectedLeads([]);
        }
    };

    // Handle individual lead selection
    const handleLeadSelect = (lead, checked) => {
        if (checked) {
            setSelectedLeads([...selectedLeads, lead]);
        } else {
            setSelectedLeads(selectedLeads.filter(l => l._id !== lead._id));
        }
    };

    // Handle assignment success
    const handleAssignmentSuccess = () => {
        setSelectedLeads([]);
        setSelectAll(false);
        fetchLeadsFromServer();
    };

    // Handle bulk assign
    const handleBulkAssign = () => {
        if (selectedLeads.length === 0) {
            toast.error("Please select leads to assign");
            return;
        }
        setShowBulkAssignModal(true);
    };

    // Handle bulk unassign
    const handleBulkUnassign = () => {
        if (selectedLeads.length === 0) {
            toast.error("Please select leads to unassign");
            return;
        }
        setShowBulkUnassignModal(true);
    };

    // Handle unassignment success
    const handleUnassignmentSuccess = () => {
        setShowBulkUnassignModal(false);
        setSelectedLeads([]);
        setSelectAll(false);
        fetchLeadsFromServer();
    };

    // Handle edit lead
    const handleEdit = (lead) => {
        // Navigate to edit page or open edit modal
        window.location.href = `/leads/edit/${lead._id}`;
    };

    // Handle delete lead
    const handleDelete = (leadId) => {
        // You can add delete functionality here if needed
        // For now, just show a message
        toast.info("Delete functionality can be added if needed");
    };

    // Handle lead selection (for LeadsTable)
    const isLeadSelected = (leadId) => {
        return selectedLeads.some(lead => lead._id === leadId);
    };

    const handleSelectLead = (lead, checked) => {
        handleLeadSelect(lead, checked);
    };

    // Fetch all affiliates with counts based on current filters
    useEffect(() => {
        // Set mounted flag to true when effect runs
        isMountedRef.current = true;

        const fetchAffiliatesWithCounts = async () => {
            try {
                // Build filters same as fetchLeadsFromServer but without pagination
                const filters = {
                    ...currentFilters,
                    hasAffiliate: true, // Only leads with affiliate field
                    ...(searchTerm.trim() && { searchTerm: searchTerm.trim() })
                };

                // Remove undefined and empty values
                Object.keys(filters).forEach(key => {
                    if (filters[key] === undefined || 
                        filters[key] === null ||
                        (Array.isArray(filters[key]) && filters[key].length === 0) ||
                        (typeof filters[key] === 'string' && filters[key].trim() === '')) {
                        delete filters[key];
                    }
                });

                // Fetch multiple pages to get all affiliates (up to 3000 leads = 300 pages of 10)
                // This ensures we capture all affiliates even if there are many
                const maxPages = 10; // Fetch up to 3000 leads
                const pageSize = 30;
                const affiliateCounts = {};
                let hasMore = true;
                let page = 1;

                while (hasMore && page <= maxPages && isMountedRef.current) {
                    // Check if component is still mounted before each API call
                    if (!isMountedRef.current) {
                        break;
                    }

                    const response = await fetchAvailableLeads(page, pageSize, filters);
                    
                    // Check again after async operation
                    if (!isMountedRef.current) {
                        break;
                    }
                    
                    if (response?.data && response.data.length > 0) {
                        // Count affiliates from fetched leads
                        response.data.forEach(lead => {
                            if (lead.affiliate && typeof lead.affiliate === 'string' && lead.affiliate.trim() !== '') {
                                affiliateCounts[lead.affiliate] = (affiliateCounts[lead.affiliate] || 0) + 1;
                            }
                        });
                        
                        // Check if there are more pages
                        if (response.pagination) {
                            hasMore = page < response.pagination.totalPages;
                        } else {
                            hasMore = response.data.length === pageSize; // If we got a full page, might have more
                        }
                        page++;
                    } else {
                        hasMore = false;
                    }
                }
                
                // Only update state if component is still mounted
                if (isMountedRef.current) {
                    // Convert to array and sort by count (descending)
                    const affiliatesArray = Object.entries(affiliateCounts)
                        .map(([affiliate, count]) => ({ affiliate, count }))
                        .sort((a, b) => b.count - a.count);
                    
                    setAllAffiliatesWithCounts(affiliatesArray);
                }
            } catch (error) {
                // Only handle error if component is still mounted
                if (isMountedRef.current) {
                    console.error("Error fetching affiliates with counts:", error);
                    // Fallback to current page affiliates
                    const affiliateCounts = {};
                    leads.forEach(lead => {
                        if (lead.affiliate && typeof lead.affiliate === 'string' && lead.affiliate.trim() !== '') {
                            affiliateCounts[lead.affiliate] = (affiliateCounts[lead.affiliate] || 0) + 1;
                        }
                    });
                    setAllAffiliatesWithCounts(Object.entries(affiliateCounts).map(([affiliate, count]) => ({ affiliate, count })));
                }
            }
        };

        fetchAffiliatesWithCounts();

        // Cleanup function: set flag to false when component unmounts or dependencies change
        return () => {
            isMountedRef.current = false;
        };
    }, [currentFilters, searchTerm]); // Re-fetch when filters change (removed leads.length dependency)

    // Get unique affiliates for display (from all affiliates with counts)
    const uniqueAffiliates = useMemo(() => {
        return allAffiliatesWithCounts.map(item => item.affiliate);
    }, [allAffiliatesWithCounts]);

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setCurrentFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setCurrentPage(1); // Reset to page 1 when filters change
    };

    // Handle clear filters
    const handleClearFilters = () => {
        setCurrentFilters({});
        setSearchTerm("");
        setCurrentPage(1);
    };

    // Count active filters
    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (searchTerm.trim()) count++;
        if (currentFilters.affiliate && currentFilters.affiliate.length > 0) count++;
        if (currentFilters.statuses && currentFilters.statuses.length > 0) count++;
        if (currentFilters.country && currentFilters.country.length > 0) count++;
        if (currentFilters.registeredFrom || currentFilters.registeredTo) count++;
        return count;
    }, [searchTerm, currentFilters]);

    if (user?.position?.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-8">
                <Card className="max-w-md mx-auto">
                    <CardContent className="text-center py-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                        <p className="text-gray-600">Administrator privileges required to access this page.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[1600px] mx-auto">
            <div className="mb-6">
                {/* Page Title - Matching Leads page style */}
                <div className="mb-4">
                    <h1 className="text-2xl font-semibold text-gray-900">Live Leads</h1>
                    <p className="text-sm text-gray-600 mt-1">Manage leads submitted by affiliates</p>
                </div>

                {/* Search and Filter Row - Matching Leads page style */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-3">
                    <div className="relative flex-1 w-full sm:max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Search by name, email, phone..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="pl-10"
                        />
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors relative"
                    >
                        <Filter className="h-4 w-4 mr-2" />
                        Filters
                        {activeFiltersCount > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-blue-600 rounded-full">
                                {activeFiltersCount}
                            </span>
                        )}
                        {showFilters ? (
                            <ChevronUp className="h-4 w-4 ml-2" />
                        ) : (
                            <ChevronDown className="h-4 w-4 ml-2" />
                        )}
                    </button>
                    {activeFiltersCount > 0 && (
                        <button
                            onClick={handleClearFilters}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                        >
                            <X className="h-4 w-4 mr-2" />
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* Action Buttons Row - Matching Leads page style */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    {/* Bulk Actions Dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={selectedLeads.length === 0}
                            >
                                Bulk Actions ({selectedLeads.length})
                                <ChevronDown className="h-4 w-4 ml-2" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem
                                onClick={handleBulkAssign}
                                disabled={selectedLeads.length === 0}
                                className="cursor-pointer"
                            >
                                <UserPlus className="h-4 w-4 mr-2" />
                                Bulk Assign
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={handleBulkUnassign}
                                disabled={selectedLeads.length === 0}
                                className="cursor-pointer"
                            >
                                <UserMinus className="h-4 w-4 mr-2" />
                                Bulk Unassign
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Stats Summary - Inline with actions */}
                    <div className="flex items-center gap-4 ml-auto text-sm text-gray-600">
                        <span>
                            <span className="font-semibold text-gray-900">{pagination?.totalCount?.toLocaleString() || leads.length.toLocaleString()}</span> total leads
                        </span>
                        <span>
                            <span className="font-semibold text-gray-900">{uniqueAffiliates.length}</span> affiliates
                        </span>
                        <span>
                            <span className="font-semibold text-gray-900">{leads.filter(l => !l.agent && !l.desk).length}</span> unassigned
                        </span>
                    </div>
                </div>

                {/* Compact Filter Panel */}
                {showFilters && (
                    <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs font-medium text-gray-700">Affiliate</Label>
                                <MultiSelect
                                    options={affiliateOptions}
                                    selected={currentFilters.affiliate || []}
                                    onChange={(selected) => handleFilterChange('affiliate', selected)}
                                    placeholder="Select affiliates"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs font-medium text-gray-700">Status</Label>
                                <MultiSelect
                                    options={statusOptions}
                                    selected={currentFilters.statuses || []}
                                    onChange={(selected) => handleFilterChange('statuses', selected)}
                                    placeholder="Select statuses"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs font-medium text-gray-700">Country</Label>
                                <MultiSelect
                                    options={countryOptions}
                                    selected={currentFilters.country || []}
                                    onChange={(selected) => handleFilterChange('country', selected)}
                                    placeholder="Select countries"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs font-medium text-gray-700">Date Range</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    <Input
                                        type="date"
                                        value={currentFilters.registeredFrom || ""}
                                        onChange={(e) => handleFilterChange('registeredFrom', e.target.value)}
                                        className="h-8 text-xs"
                                    />
                                    <Input
                                        type="date"
                                        value={currentFilters.registeredTo || ""}
                                        onChange={(e) => handleFilterChange('registeredTo', e.target.value)}
                                        className="h-8 text-xs"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Compact Affiliate Badges */}
                {uniqueAffiliates.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1.5 items-center">
                        <span className="text-xs font-medium text-gray-600">Affiliates:</span>
                        {allAffiliatesWithCounts.map(({ affiliate, count }) => (
                            <Badge key={affiliate} variant="outline" className="text-[10px] px-1.5 py-0.5">
                                {affiliate} ({count.toLocaleString()})
                            </Badge>
                        ))}
                    </div>
                )}
            </div>

            {/* Compact Selection Info */}
            {filteredLeads.length > 0 && (
                <div className="mb-3 p-2.5 bg-blue-50/50 border border-blue-200/60 rounded">
                    <div className="flex items-center space-x-2">
                        <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} className="h-4 w-4" />
                        <span className="text-xs font-medium text-gray-700">
                            Select all {pagination?.totalCount ? pagination.totalCount.toLocaleString() : filteredLeads.length} leads
                        </span>
                        {selectedLeads.length > 0 && (
                            <span className="text-xs text-blue-600 font-medium ml-2">
                                ({selectedLeads.length} selected)
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Leads Table */}
            <LeadsTable
                leads={filteredLeads}
                loading={loading}
                selectedLeads={selectedLeads}
                onSelectLead={handleSelectLead}
                isLeadSelected={isLeadSelected}
                showBulkSelection={true}
                onEdit={handleEdit}
                onSendEmail={setEmailLead}
                onDelete={handleDelete}
                onLeadUpdate={fetchLeadsFromServer}
                serverSidePagination={true}
                pagination={pagination}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
            />

            {/* Bulk Assign Modal */}
            <BulkAssignModal
                open={showBulkAssignModal}
                onClose={() => setShowBulkAssignModal(false)}
                selectedLeads={selectedLeads}
                onSuccess={handleAssignmentSuccess}
            />

            {/* Bulk Unassign Modal */}
            <BulkUnassignModal
                open={showBulkUnassignModal}
                onClose={() => setShowBulkUnassignModal(false)}
                selectedLeads={selectedLeads}
                onSuccess={handleUnassignmentSuccess}
            />

            <ComposeEmailDialog
                open={!!emailLead}
                onClose={() => setEmailLead(null)}
                lead={emailLead}
            />
        </div>
    );
}

